/**
 * BuriedVerse — THREE diorama-native overlay CONCEPTS (2026-07-22, round 2).
 *
 * Round 1 was bible_decoder's exact skeleton in a warm coat — Yash called it: still reads
 * as the sibling channel. The move here is to stop floating text over the scene and make
 * the overlay a PHYSICAL OBJECT in the miniature world. A diorama is a made thing on a
 * table; what belongs beside it is a card, a caption plate, or a pinned tag.
 *
 * Biggest single differentiator from bible_decoder: text goes DARK-ON-LIGHT (printed on a
 * paper surface) instead of light-on-dark floating. That also kills the legibility problem
 * outright — the card carries its own surface, so it never fights the brushwork.
 *
 * All three render the SAME plate + SAME content so they can be compared directly.
 * Applied in all three per Yash: gloss is sentence-capitalised ("To be anxious…"),
 * and the BURIEDVERSE wordmark moved from top-right to BOTTOM.
 */
import { AbsoluteFill, Img, staticFile, useCurrentFrame, interpolate, Easing } from "remotion";
import { loadFont as loadSerif } from "@remotion/google-fonts/EBGaramond";
import { loadFont as loadSans } from "@remotion/google-fonts/Lato";

const { fontFamily: serif } = loadSerif("normal", {
  weights: ["400", "500", "600"],
  subsets: ["latin", "greek"],
});
const { fontFamily: sans } = loadSans("normal", { weights: ["400", "700"], subsets: ["latin"] });

// ── paper + ink (the inversion: dark ink ON light paper) ──
const PAPER_HI = "#f2ead8";
const PAPER_LO = "#e2d6bd";
const INK = "#241a12";
const INK_SOFT = "#6b5844";
const RULE = "rgba(90,68,44,0.42)";
const GOLD = "#b8862f";
const CARD_SHADOW = "0 20px 44px rgba(12,7,3,0.52), 0 4px 10px rgba(12,7,3,0.40)";

// shared content — identical across concepts so the comparison is honest
const KICKER = "The word";
const WORD = "μεριμνάω";
const TRANSLIT = "merimnaō";
const GLOSS = "To be anxious, to be weighed down by care.";
const TAG_KICKER = "Citation";
const TAG_VALUE = "James 1:6";

const useIn = (delay: number, dur = 16) => {
  const frame = useCurrentFrame();
  return interpolate(frame, [delay, delay + dur], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
};

// ── BURIEDVERSE wordmark — MOVED TO BOTTOM per Yash. Placeholder text, not a logo design. ──
const Wordmark: React.FC<{ bottom?: number }> = ({ bottom = 26 }) => {
  const o = useIn(0, 30);
  return (
    <div
      style={{
        position: "absolute",
        bottom,
        right: 46,
        opacity: o * 0.72,
        fontFamily: serif,
        fontSize: 16,
        letterSpacing: 5.5,
        color: "#f7f0e3",
        textTransform: "uppercase",
        textShadow: "0 1px 4px rgba(20,12,6,0.9), 0 0 16px rgba(20,12,6,0.5)",
      }}
    >
      BuriedVerse
    </div>
  );
};

const paperBg = `linear-gradient(158deg, ${PAPER_HI} 0%, ${PAPER_HI} 42%, ${PAPER_LO} 100%)`;
// faint fibre texture so it reads as paper, not a flat UI panel
const paperTexture =
  "repeating-linear-gradient(94deg, rgba(120,95,60,0.045) 0px, rgba(120,95,60,0.045) 1px, rgba(0,0,0,0) 1px, rgba(0,0,0,0) 4px)";

const Plate: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AbsoluteFill style={{ background: "#000" }}>
    <Img src={staticFile("bv_demo/p1_merimnao.jpg")} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
    {children}
  </AbsoluteFill>
);

/* ══════════════════════════════════════════════════════════════════════════
   CONCEPT A — MUSEUM SPECIMEN CARD
   The diorama is a museum object; the specimen card is its canonical companion.
   Straight, printed, catalogued. Reads as evidence.
   ══════════════════════════════════════════════════════════════════════════ */
export const BV_ConceptA_Specimen: React.FC = () => {
  const o = useIn(4);
  const y = interpolate(useCurrentFrame(), [4, 24], [14, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  const oTag = useIn(0);
  return (
    <Plate>
      {/* catalogue chit — top left */}
      <div
        style={{
          position: "absolute",
          top: 44,
          left: 48,
          opacity: oTag,
          background: paperBg,
          backgroundImage: `${paperTexture}, ${paperBg}`,
          border: "1px solid rgba(90,68,44,0.30)",
          boxShadow: CARD_SHADOW,
          padding: "12px 18px 13px",
        }}
      >
        <div
          style={{
            fontFamily: sans,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: 2.8,
            color: GOLD,
            textTransform: "uppercase",
          }}
        >
          {TAG_KICKER}
        </div>
        <div style={{ fontFamily: serif, fontSize: 22, color: INK, marginTop: 2 }}>{TAG_VALUE}</div>
      </div>

      {/* specimen card — bottom left */}
      <div
        style={{
          position: "absolute",
          left: 60,
          bottom: 74,
          width: 486,
          opacity: o,
          transform: `translateY(${y}px)`,
          background: paperBg,
          backgroundImage: `${paperTexture}, ${paperBg}`,
          border: "1px solid rgba(90,68,44,0.30)",
          boxShadow: CARD_SHADOW,
          padding: "22px 28px 26px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div
            style={{
              fontFamily: sans,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 3.2,
              color: INK_SOFT,
              textTransform: "uppercase",
            }}
          >
            Specimen · {KICKER}
          </div>
          <div style={{ fontFamily: sans, fontSize: 9, letterSpacing: 1.6, color: GOLD }}>No. 014</div>
        </div>
        <div style={{ height: 1, background: RULE, margin: "11px 0 15px" }} />
        <div style={{ fontFamily: serif, fontSize: 60, lineHeight: 1.0, color: INK }}>{WORD}</div>
        <div style={{ fontFamily: serif, fontSize: 24, fontStyle: "italic", color: INK_SOFT, marginTop: 5 }}>
          {TRANSLIT}
        </div>
        <div style={{ fontFamily: sans, fontSize: 16.5, lineHeight: 1.45, color: INK, marginTop: 11 }}>{GLOSS}</div>
      </div>
      <Wordmark />
    </Plate>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   CONCEPT B — PAINTED CAPTION BAND
   The old illustrated-Bible plate convention: a caption band under the picture.
   Most integrated into the art, least "object".
   ══════════════════════════════════════════════════════════════════════════ */
export const BV_ConceptB_CaptionBand: React.FC = () => {
  const o = useIn(4);
  const w = interpolate(useCurrentFrame(), [6, 30], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  const oTag = useIn(0);
  return (
    <Plate>
      {/* fact line — plain, on the scene, so the band stays the star */}
      <div style={{ position: "absolute", top: 46, left: 52, opacity: oTag }}>
        <span
          style={{
            fontFamily: sans,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 3.2,
            color: "#e8c98a",
            textTransform: "uppercase",
            textShadow: "0 1px 4px rgba(20,12,6,0.95)",
          }}
        >
          {TAG_KICKER} ·{" "}
        </span>
        <span
          style={{
            fontFamily: serif,
            fontSize: 22,
            color: "#f7f0e3",
            textShadow: "0 1px 4px rgba(20,12,6,0.95)",
          }}
        >
          {TAG_VALUE}
        </span>
      </div>

      {/* caption band */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 58,
          opacity: o,
          background: `linear-gradient(180deg, rgba(242,234,216,0.95) 0%, rgba(226,214,189,0.95) 100%)`,
          backgroundImage: `${paperTexture}, linear-gradient(180deg, rgba(242,234,216,0.95) 0%, rgba(226,214,189,0.95) 100%)`,
          borderTop: `2px solid ${RULE}`,
          borderBottom: `2px solid ${RULE}`,
          padding: "16px 0 19px",
          textAlign: "center",
          boxShadow: "0 -8px 30px rgba(12,7,3,0.35)",
        }}
      >
        <div
          style={{
            width: `${w * 100}%`,
            maxWidth: 190,
            height: 1,
            background: GOLD,
            margin: "0 auto 10px",
            opacity: 0.8,
          }}
        />
        <div
          style={{
            fontFamily: sans,
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: 3.4,
            color: INK_SOFT,
            textTransform: "uppercase",
          }}
        >
          {KICKER}
        </div>
        <div style={{ marginTop: 7 }}>
          <span style={{ fontFamily: serif, fontSize: 46, color: INK, lineHeight: 1.05 }}>{WORD}</span>
          <span style={{ fontFamily: serif, fontSize: 26, color: INK_SOFT, margin: "0 14px" }}>·</span>
          <span style={{ fontFamily: serif, fontSize: 30, fontStyle: "italic", color: INK_SOFT }}>{TRANSLIT}</span>
        </div>
        <div style={{ fontFamily: sans, fontSize: 16.5, color: INK, marginTop: 8 }}>{GLOSS}</div>
      </div>
      <Wordmark bottom={20} />
    </Plate>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   CONCEPT C — PINNED PAPER TAG
   A label physically pinned to the miniature. Most obviously "diorama".
   ══════════════════════════════════════════════════════════════════════════ */
const Pin: React.FC = () => (
  <div
    style={{
      position: "absolute",
      top: -7,
      left: 20,
      width: 15,
      height: 15,
      borderRadius: "50%",
      background: "radial-gradient(circle at 34% 30%, #f0d9a4 0%, #b8862f 45%, #6d4c14 100%)",
      boxShadow: "0 3px 7px rgba(12,7,3,0.65)",
    }}
  />
);

export const BV_ConceptC_PinnedTag: React.FC = () => {
  const o = useIn(4);
  const rot = interpolate(useCurrentFrame(), [4, 30], [-4.2, -1.6], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  const oTag = useIn(0);
  return (
    <Plate>
      {/* pinned chit */}
      <div
        style={{
          position: "absolute",
          top: 50,
          left: 52,
          opacity: oTag,
          transform: "rotate(1.4deg)",
          background: paperBg,
          backgroundImage: `${paperTexture}, ${paperBg}`,
          boxShadow: CARD_SHADOW,
          padding: "11px 20px 12px",
        }}
      >
        <Pin />
        <div
          style={{
            fontFamily: sans,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: 2.8,
            color: GOLD,
            textTransform: "uppercase",
          }}
        >
          {TAG_KICKER}
        </div>
        <div style={{ fontFamily: serif, fontSize: 22, color: INK, marginTop: 2 }}>{TAG_VALUE}</div>
      </div>

      {/* pinned hero tag */}
      <div
        style={{
          position: "absolute",
          left: 68,
          bottom: 78,
          width: 470,
          opacity: o,
          transform: `rotate(${rot}deg)`,
          transformOrigin: "28px 0px",
          background: paperBg,
          backgroundImage: `${paperTexture}, ${paperBg}`,
          boxShadow: CARD_SHADOW,
          padding: "24px 28px 26px",
        }}
      >
        <Pin />
        <div
          style={{
            fontFamily: sans,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 3.2,
            color: INK_SOFT,
            textTransform: "uppercase",
          }}
        >
          {KICKER}
        </div>
        <div style={{ height: 1, background: RULE, margin: "10px 0 14px", width: 120 }} />
        <div style={{ fontFamily: serif, fontSize: 58, lineHeight: 1.0, color: INK }}>{WORD}</div>
        <div style={{ fontFamily: serif, fontSize: 24, fontStyle: "italic", color: INK_SOFT, marginTop: 5 }}>
          {TRANSLIT}
        </div>
        <div style={{ fontFamily: sans, fontSize: 16.5, lineHeight: 1.45, color: INK, marginTop: 11 }}>{GLOSS}</div>
      </div>
      <Wordmark />
    </Plate>
  );
};

/* ── Round-1 layout, corrected only (capitalised gloss + wordmark moved to bottom).
      Kept so the comparison includes "what we already had, fixed". ── */
export const BV_ConceptD_R1Fixed: React.FC = () => {
  const o = useIn(4);
  const rule = interpolate(useCurrentFrame(), [8, 30], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  const oTag = useIn(0);
  const SOFT = "0 1px 3px rgba(20,12,6,0.85), 0 2px 14px rgba(20,12,6,0.45)";
  return (
    <Plate>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 520,
          height: 190,
          opacity: oTag,
          background:
            "linear-gradient(135deg, rgba(22,14,7,0.72) 0%, rgba(22,14,7,0.34) 45%, rgba(22,14,7,0) 78%)",
        }}
      />
      <div style={{ position: "absolute", top: 42, left: 46, opacity: oTag }}>
        <div
          style={{
            fontFamily: sans,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 3.4,
            color: "#d9a441",
            textTransform: "uppercase",
            textShadow: SOFT,
          }}
        >
          {TAG_KICKER}
        </div>
        <div style={{ fontFamily: serif, fontSize: 27, color: "#f7f0e3", marginTop: 3, textShadow: SOFT }}>
          {TAG_VALUE}
        </div>
      </div>
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 300,
          opacity: o,
          background:
            "linear-gradient(to top, rgba(20,12,6,0.90) 0%, rgba(20,12,6,0.66) 38%, rgba(20,12,6,0.22) 72%, rgba(20,12,6,0) 100%)",
        }}
      />
      <div style={{ position: "absolute", left: 60, right: 60, bottom: 62, opacity: o }}>
        <div
          style={{
            fontFamily: sans,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 3.6,
            color: "#d9a441",
            textTransform: "uppercase",
            textShadow: SOFT,
          }}
        >
          {KICKER}
        </div>
        <div
          style={{
            height: 1.5,
            width: `${rule * 100}%`,
            maxWidth: 148,
            background: "#d9a441",
            opacity: 0.85,
            margin: "7px 0 13px",
          }}
        />
        <div style={{ fontFamily: serif, fontSize: 66, lineHeight: 1.02, color: "#f7f0e3", textShadow: SOFT }}>
          {WORD}
        </div>
        <div
          style={{
            fontFamily: serif,
            fontSize: 26,
            fontStyle: "italic",
            color: "#d3c4a9",
            marginTop: 6,
            textShadow: SOFT,
          }}
        >
          {TRANSLIT}
        </div>
        <div style={{ fontFamily: sans, fontSize: 19, color: "#f7f0e3", marginTop: 9, textShadow: SOFT }}>{GLOSS}</div>
      </div>
      <Wordmark bottom={22} />
    </Plate>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
